import type { Choice } from '../types/api';
import { Icon } from '../ui';
import styles from './ChoiceCard.module.css';

interface Props {
  choices: Choice[];
  /** The option the user already picked: it gets a tick and the others dim. */
  chosen?: string;
  /** Called with the option's label, which is sent as the user's message. */
  onChoose?: (label: string) => void;
  /** Puts the cursor in the input, for an answer that is none of the options. */
  onTypeOwn?: () => void;
  disabled?: boolean;
}

/**
 * The options under a setup question (#489), drawn as the answer's own numbered list
 * on its text column, so they read as part of the reply. One click sends an option as
 * the user's message.
 */
export default function ChoiceCard({ choices, chosen, onChoose, onTypeOwn, disabled }: Props) {
  const answered = chosen !== undefined;
  return (
    <div className={styles.column}>
      <ol className={styles.list}>
        {choices.map((c, i) => {
          const picked = c.label === chosen;
          const state = !answered ? '' : picked ? styles.picked : styles.dim;
          return (
            <li key={c.label}>
              <button
                type="button"
                className={`${styles.item} ${state}`}
                style={{ animationDelay: `${i * 45}ms` }}
                disabled={answered || disabled}
                aria-pressed={answered ? picked : undefined}
                onClick={() => onChoose?.(c.label)}
              >
                <span className={styles.num} aria-hidden="true">
                  {picked ? <Icon name="check" size={12} /> : i + 1}
                </span>
                <span>
                  <span className={styles.label}>{c.label}</span>
                  {!answered && c.hint && <span className={styles.hint}>{c.hint}</span>}
                </span>
                {!answered && <Icon name="arrow-right" size={16} className={styles.arrow} />}
              </button>
            </li>
          );
        })}
      </ol>
      {!answered && (
        <p className={styles.own}>
          Or{' '}
          <button type="button" className={styles.ownLink} onClick={onTypeOwn}>
            type your own answer
          </button>
        </p>
      )}
    </div>
  );
}
